import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ImageUploadBox, ImageUploader } from '@service/seller/components/ImageUploadBox';
import {
  giftChoiceUnitListDataAtom,
  giftChoiceUnitListStatusAtom,
} from '@shared/api/giftChoiceUnit/giftChoiceUnit.atom';
import { useGiftChoiceUnitListHook } from '@shared/api/giftChoiceUnit/giftChoiceUnit.hook';
import {
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryListStatusAtom,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import {
  giftItemInfoDataAtomFamily,
  giftItemInfoStatusAtomFamily,
} from '@shared/api/giftItemInfo/giftItemInfo.atom';
import {
  createGiftItemInfo,
  deleteGiftItemInfo,
  updateGiftItemInfo,
} from '@shared/api/giftItemInfo/giftItemInfo.controller';
import { useGiftItemInfoHook } from '@shared/api/giftItemInfo/giftItemInfo.hook';
import {
  AdditionalOption,
  GiftItemInfoCreate,
  GiftItemInfoType,
  GiftItemInfoUpdate,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { postImage } from '@shared/api/uploadImage';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useNavigate, useParams } from 'react-router-dom';

const genKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `opt-${Math.random().toString(36).slice(2)}`;

// 추가옵션 복사/붙여넣기 클립보드 (상품 간 공유, localStorage 기반)
const OPTIONS_CLIPBOARD_KEY = 'giftItemInfo:additionalOptions:clipboard';

type ClipboardChoice = {
  name?: string;
  priceDelta?: number;
  description?: string;
  colorHex?: string;
  enabled?: boolean;
  descriptionImage?: Image | null;
};
type ClipboardOption = {
  name?: string;
  required?: boolean;
  selectionCount?: number;
  slotLabels?: string[];
  choices?: ClipboardChoice[];
};

/** 폼 내부에서 다루는 추가옵션 선택종류 (신규 파일/기존 이미지 상태 포함) */
interface OptionChoiceForm {
  key: string; // 렌더 안정용 로컬 키
  name: string;
  priceDelta: number;
  description: string; // 한 줄 설명
  colorHex: string; // 색상 핵사코드 (빈 문자열이면 미사용)
  enabled: boolean;
  descriptionImage: Image | null; // 기존 populated 이미지
  file: File | null; // 새로 선택한 파일
  preview: string; // 새 파일 object URL
}

/** 폼 내부에서 다루는 추가옵션 그룹 */
interface AdditionalOptionForm {
  key: string;
  name: string;
  required: boolean;
  selectionCount: number; // 선택 슬롯 수 (기본 1)
  slotLabels: string[]; // 슬롯별 라벨 (선택, 비우면 name+번호로 생성)
  choices: OptionChoiceForm[];
}

const createEmptyChoice = (): OptionChoiceForm => ({
  key: genKey(),
  name: '',
  priceDelta: 0,
  description: '',
  colorHex: '',
  enabled: true,
  descriptionImage: null,
  file: null,
  preview: '',
});

const createEmptyOption = (): AdditionalOptionForm => ({
  key: genKey(),
  name: '',
  required: false,
  selectionCount: 1,
  slotLabels: [],
  choices: [createEmptyChoice()],
});

interface FormData {
  type: GiftItemInfoType;
  giftItemBrand: string; // 상품 브랜드 ID
  giftDeliveryCategory: string; // 배송 카테고리 ID
  name: string;
  priceRegular: number;
  priceRetail: number;
  priceWhole: number;
  isEnabled: boolean;
  isSelectable: boolean; // 선택형 상품 여부
  isTaxable: boolean; // 과세여부
  detailInfo: string;
  tags: string;
  // 쿠폰 상세
  couponValidPeriod: string;
  couponPlaceOfExchange: string;
  // 배송 상세
  deliveryShippingInfo: string;
  deliveryShippingFeeInfo: string;
  deliveryShippingFee: number | ''; // 배송비 (원)
  deliveryFreeShippingMinQuantity: number | ''; // 무료배송 최소수량
  allowIndividualDelivery: boolean; // 개별배송 허용 여부 (false면 일괄배송 전용)
  // 주문 설정
  minOrderQuantity: number;
  orderUnit: number;
  order: number | ''; // 표시 순서
  productCode: string | ''; // 상품코드
}

export const GiftItemInfoFormPage: FC = function GiftItemInfoFormPage() {
  const { itemInfoId } = useParams<{ itemInfoId?: string }>();
  const isEdit = itemInfoId && itemInfoId !== 'new';
  const navigate = useNavigate();

  const { sellerRole } = useAuth();

  // Hooks
  useGiftItemInfoHook(isEdit ? itemInfoId ?? '' : '', sellerRole());
  useGiftItemBrandListHook(sellerRole());
  useGiftDeliveryCategoryListHook(sellerRole());
  useGiftChoiceUnitListHook(sellerRole());

  // State
  const giftItemInfoStatus = useRecoilValue(
    giftItemInfoStatusAtomFamily(itemInfoId ?? ''),
  );
  const giftItemInfoData = useRecoilValue(giftItemInfoDataAtomFamily(itemInfoId ?? ''));

  // 브랜드 목록
  const brandListStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const brandList = useRecoilValue(giftItemBrandListDataAtom);

  // 배송 카테고리 목록
  const deliveryCategoryListStatus = useRecoilValue(giftDeliveryCategoryListStatusAtom);
  const deliveryCategoryList = useRecoilValue(giftDeliveryCategoryListDataAtom);

  // 선택유닛 목록
  const choiceUnitListStatus = useRecoilValue(giftChoiceUnitListStatusAtom);
  const choiceUnitList = useRecoilValue(giftChoiceUnitListDataAtom);

  const [formData, setFormData] = useState<FormData>({
    type: GiftItemInfoType.Delivery,
    giftItemBrand: '',
    giftDeliveryCategory: '',
    name: '',
    priceRegular: 0,
    priceRetail: 0,
    priceWhole: 0,
    isEnabled: true,
    isSelectable: false,
    isTaxable: false,
    detailInfo: '',
    tags: '',
    couponValidPeriod: '',
    couponPlaceOfExchange: '',
    deliveryShippingInfo: '',
    deliveryShippingFeeInfo: '',
    deliveryShippingFee: '',
    deliveryFreeShippingMinQuantity: '',
    allowIndividualDelivery: true,
    minOrderQuantity: 1,
    orderUnit: 1,
    order: '',
    productCode: '',
  });

  // 선택된 브랜드
  const selectedBrand = useMemo(
    () => brandList.find((b) => b._id === formData.giftItemBrand),
    [brandList, formData.giftItemBrand],
  );

  // 선택유닛 선택 상태
  const [selectedChoiceUnits, setSelectedChoiceUnits] = useState<string[]>([]);

  // 추가옵션 상태
  const [additionalOptions, setAdditionalOptions] = useState<AdditionalOptionForm[]>([]);

  // 언마운트 시 생성한 object URL 정리
  useEffect(() => {
    return () => {
      additionalOptions.forEach((opt) =>
        opt.choices.forEach((c) => c.preview && URL.revokeObjectURL(c.preview)),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 옵션 그룹 헬퍼
  const addOption = () =>
    setAdditionalOptions((prev) => [...prev, createEmptyOption()]);

  const removeOption = (optKey: string) =>
    setAdditionalOptions((prev) => {
      const target = prev.find((o) => o.key === optKey);
      target?.choices.forEach((c) => c.preview && URL.revokeObjectURL(c.preview));
      return prev.filter((o) => o.key !== optKey);
    });

  const updateOption = (optKey: string, patch: Partial<AdditionalOptionForm>) =>
    setAdditionalOptions((prev) =>
      prev.map((o) => (o.key === optKey ? { ...o, ...patch } : o)),
    );

  const moveOption = (optKey: string, dir: -1 | 1) =>
    setAdditionalOptions((prev) => {
      const idx = prev.findIndex((o) => o.key === optKey);
      const next = idx + dir;
      if (idx < 0 || next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      const tmp = arr[idx] as AdditionalOptionForm;
      arr[idx] = arr[next] as AdditionalOptionForm;
      arr[next] = tmp;
      return arr;
    });

  // 선택종류 헬퍼
  const addChoice = (optKey: string) =>
    setAdditionalOptions((prev) =>
      prev.map((o) =>
        o.key === optKey ? { ...o, choices: [...o.choices, createEmptyChoice()] } : o,
      ),
    );

  const removeChoice = (optKey: string, choiceKey: string) =>
    setAdditionalOptions((prev) =>
      prev.map((o) => {
        if (o.key !== optKey) return o;
        const target = o.choices.find((c) => c.key === choiceKey);
        if (target?.preview) URL.revokeObjectURL(target.preview);
        return { ...o, choices: o.choices.filter((c) => c.key !== choiceKey) };
      }),
    );

  const updateChoice = (
    optKey: string,
    choiceKey: string,
    patch: Partial<OptionChoiceForm>,
  ) =>
    setAdditionalOptions((prev) =>
      prev.map((o) =>
        o.key === optKey
          ? {
              ...o,
              choices: o.choices.map((c) =>
                c.key === choiceKey ? { ...c, ...patch } : c,
              ),
            }
          : o,
      ),
    );

  const moveChoice = (optKey: string, choiceKey: string, dir: -1 | 1) =>
    setAdditionalOptions((prev) =>
      prev.map((o) => {
        if (o.key !== optKey) return o;
        const idx = o.choices.findIndex((c) => c.key === choiceKey);
        const next = idx + dir;
        if (idx < 0 || next < 0 || next >= o.choices.length) return o;
        const arr = [...o.choices];
        const tmp = arr[idx] as OptionChoiceForm;
        arr[idx] = arr[next] as OptionChoiceForm;
        arr[next] = tmp;
        return { ...o, choices: arr };
      }),
    );

  // 선택종류 설명 이미지 파일 선택 (object URL 미리보기)
  const handleChoiceFile = (optKey: string, choiceKey: string, file: File) => {
    const url = URL.createObjectURL(file);
    setAdditionalOptions((prev) =>
      prev.map((o) =>
        o.key === optKey
          ? {
              ...o,
              choices: o.choices.map((c) => {
                if (c.key !== choiceKey) return c;
                if (c.preview) URL.revokeObjectURL(c.preview);
                return { ...c, file, preview: url };
              }),
            }
          : o,
      ),
    );
  };

  // 선택종류 설명 이미지 표시 URL (새 파일 우선, 없으면 기존)
  const choiceImageUrl = (c: OptionChoiceForm) =>
    c.preview || (c.descriptionImage ? fileBucketUrl(c.descriptionImage.url, 'raw') : '');

  // 추가옵션 복사/붙여넣기 (localStorage) — 클립보드에 담긴 옵션 그룹 수
  const [clipboardCount, setClipboardCount] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(OPTIONS_CLIPBOARD_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      return 0;
    }
  });

  // 현재 옵션을 클립보드에 복사 (파일 미리보기는 제외, 기존 이미지 참조는 유지)
  const handleCopyOptions = () => {
    if (additionalOptions.length === 0) return;
    const serializable = additionalOptions.map((o) => ({
      name: o.name,
      required: o.required,
      selectionCount: o.selectionCount,
      slotLabels: o.slotLabels,
      choices: o.choices.map((c) => ({
        name: c.name,
        priceDelta: c.priceDelta,
        description: c.description,
        colorHex: c.colorHex,
        enabled: c.enabled,
        descriptionImage: c.descriptionImage, // 기존 업로드 이미지(Image) 참조 유지
      })),
    }));
    try {
      localStorage.setItem(OPTIONS_CLIPBOARD_KEY, JSON.stringify(serializable));
      setClipboardCount(serializable.length);
      alert(`추가옵션 ${serializable.length}개를 복사했습니다.`);
    } catch {
      alert('복사에 실패했습니다.');
    }
  };

  // 클립보드의 옵션을 현재 목록 끝에 붙여넣기
  const handlePasteOptions = () => {
    let parsed: unknown;
    try {
      const raw = localStorage.getItem(OPTIONS_CLIPBOARD_KEY);
      parsed = raw ? JSON.parse(raw) : null;
    } catch {
      parsed = null;
    }
    if (!Array.isArray(parsed) || parsed.length === 0) {
      alert('복사된 추가옵션이 없습니다.');
      return;
    }
    const pasted: AdditionalOptionForm[] = (parsed as ClipboardOption[]).map((o) => ({
      key: genKey(),
      name: o?.name ?? '',
      required: o?.required ?? false,
      selectionCount: Math.max(1, o?.selectionCount ?? 1),
      slotLabels: Array.isArray(o?.slotLabels) ? o.slotLabels : [],
      choices: Array.isArray(o?.choices)
        ? o.choices.map((c) => ({
            key: genKey(),
            name: c?.name ?? '',
            priceDelta: c?.priceDelta ?? 0,
            description: c?.description ?? '',
            colorHex: c?.colorHex ?? '',
            enabled: c?.enabled ?? true,
            descriptionImage:
              c?.descriptionImage && typeof c.descriptionImage === 'object'
                ? (c.descriptionImage as Image)
                : null,
            file: null,
            preview: '',
          }))
        : [],
    }));
    setAdditionalOptions((prev) => [...prev, ...pasted]);
    alert(`추가옵션 ${pasted.length}개를 붙여넣었습니다.`);
  };

  // 선택유닛 가격대 필터
  const [priceFilterMin, setPriceFilterMin] = useState<string>('');
  const [priceFilterMax, setPriceFilterMax] = useState<string>('');

  const filteredChoiceUnitList = useMemo(() => {
    if (!choiceUnitList) return [];
    const min = priceFilterMin !== '' ? Number(priceFilterMin) : null;
    const max = priceFilterMax !== '' ? Number(priceFilterMax) : null;
    if (min === null && max === null) return choiceUnitList;
    return choiceUnitList.filter((unit) => {
      const price = unit.priceRegular ?? 0;
      if (min !== null && price < min) return false;
      if (max !== null && price > max) return false;
      return true;
    });
  }, [choiceUnitList, priceFilterMin, priceFilterMax]);

  // 선택유닛 변경 시 정가를 선택된 유닛의 최대 priceRegular로 자동 설정
  // 단, 수정 모드 최초 로드 시점에는 저장된 정가를 유지(1회 스킵)하여 덮어쓰지 않는다.
  useEffect(() => {
    if (!formData.isSelectable || selectedChoiceUnits.length === 0) return;
    if (isEdit && !priceAutoSyncReadyRef.current) {
      priceAutoSyncReadyRef.current = true;
      return;
    }
    const maxPrice = selectedChoiceUnits.reduce((max, unitId) => {
      const unit = choiceUnitList.find((u) => u._id === unitId);
      return Math.max(max, unit?.priceRegular ?? 0);
    }, 0);
    setFormData((prev) => ({ ...prev, priceRegular: maxPrice }));
  }, [selectedChoiceUnits, formData.isSelectable, choiceUnitList, isEdit]);

  // 이미지 관련 state
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [headerImageFiles, setHeaderImageFiles] = useState<File[] | null>(null);
  const [headerImagePreviews, setHeaderImagePreviews] = useState<string[]>([]);
  const [detailImageFiles, setDetailImageFiles] = useState<File[] | null>(null);
  const [detailImagePreviews, setDetailImagePreviews] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);
  // 수정 모드 최초 1회만 폼에 로드 (refetch로 인한 입력 덮어쓰기/반복 실행 방지)
  const initializedRef = useRef(false);
  // 정가 자동 동기화가 수정 로드 직후 저장값을 덮어쓰지 않도록 1회 스킵
  const priceAutoSyncReadyRef = useRef(false);

  // 모달 상태
  const openId = `giftItemInfo-${itemInfoId ?? 'new'}`;
  const headerImageOpenId = `headerImageUploader-${openId}`;
  const setHeaderImageUploaderOpen = useSetRecoilState(
    simpleOpenAtomFamily(`imageUploader-${headerImageOpenId}`),
  );
  const setDetailImageUploaderOpen = useSetRecoilState(
    simpleOpenAtomFamily(`imageUploader-${openId}`),
  );

  // 수정 모드일 때 최초 1회만 데이터 로드
  useEffect(() => {
    if (initializedRef.current) return;
    if (isEdit && giftItemInfoStatus === 'success' && giftItemInfoData) {
      setFormData({
        type: giftItemInfoData.type || GiftItemInfoType.Delivery,
        giftItemBrand:
          typeof giftItemInfoData.giftItemBrand === 'object'
            ? giftItemInfoData.giftItemBrand?._id || ''
            : giftItemInfoData.giftItemBrand || '',
        giftDeliveryCategory:
          typeof giftItemInfoData.giftDeliveryCategory === 'object'
            ? giftItemInfoData.giftDeliveryCategory?._id || ''
            : giftItemInfoData.giftDeliveryCategory || '',
        name: giftItemInfoData.name || '',
        priceRegular: giftItemInfoData.priceRegular || 0,
        priceRetail: giftItemInfoData.priceRetail || 0,
        priceWhole: giftItemInfoData.priceWhole || 0,
        isEnabled: giftItemInfoData.isEnabled ?? true,
        isSelectable: giftItemInfoData.isSelectable ?? false,
        isTaxable: giftItemInfoData.isTaxable ?? true,
        detailInfo: giftItemInfoData.detailInfo || '',
        tags: giftItemInfoData.tags?.join(', ') || '',
        couponValidPeriod: giftItemInfoData.couponDetail?.validPeriod || '',
        couponPlaceOfExchange: giftItemInfoData.couponDetail?.placeOfExchange || '',
        deliveryShippingInfo: giftItemInfoData.deliveryDetail?.shippingInfo || '',
        deliveryShippingFeeInfo: giftItemInfoData.deliveryDetail?.shippingFeeInfo || '',
        deliveryShippingFee: giftItemInfoData.deliveryDetail?.shippingFee ?? '',
        deliveryFreeShippingMinQuantity:
          giftItemInfoData.deliveryDetail?.freeShippingMinQuantity ?? '',
        allowIndividualDelivery: giftItemInfoData.allowIndividualDelivery ?? true,
        minOrderQuantity: giftItemInfoData.minOrderQuantity || 1,
        orderUnit: giftItemInfoData.orderUnit || 1,
        order: giftItemInfoData.order ?? '',
        productCode: giftItemInfoData.productCode ?? '',
      });

      if (giftItemInfoData.choiceUnits && Array.isArray(giftItemInfoData.choiceUnits)) {
        setSelectedChoiceUnits(
          giftItemInfoData.choiceUnits.map((unit) =>
            typeof unit === 'object' ? unit._id : unit,
          ),
        );
      }

      // 로드로 인해 정가 자동 동기화가 곧 한 번 실행될지 판단.
      // 실행 예정이면 그 1회를 스킵(저장값 유지)하고, 아니면 바로 활성화해
      // 이후 사용자의 첫 선택부터 정가가 동기화되도록 한다.
      const loadedUnitCount = Array.isArray(giftItemInfoData.choiceUnits)
        ? giftItemInfoData.choiceUnits.length
        : 0;
      const willAutoSyncOnLoad =
        (giftItemInfoData.isSelectable ?? false) && loadedUnitCount > 0;
      priceAutoSyncReadyRef.current = !willAutoSyncOnLoad;

      // 추가옵션 로드 (order 순 정렬)
      if (Array.isArray(giftItemInfoData.additionalOptions)) {
        const loaded = [...giftItemInfoData.additionalOptions]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map<AdditionalOptionForm>((opt) => ({
            key: genKey(),
            name: opt.name ?? '',
            required: opt.required ?? false,
            selectionCount: Math.max(1, opt.selectionCount ?? 1),
            slotLabels: Array.isArray(opt.slotLabels) ? opt.slotLabels : [],
            choices: [...(opt.choices ?? [])]
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map<OptionChoiceForm>((c) => ({
                key: genKey(),
                name: c.name ?? '',
                priceDelta: c.priceDelta ?? 0,
                description: c.description ?? '',
                colorHex: c.colorHex ?? '',
                enabled: c.enabled ?? true,
                descriptionImage:
                  c.descriptionImage && typeof c.descriptionImage === 'object'
                    ? (c.descriptionImage as Image)
                    : null,
                file: null,
                preview: '',
              })),
          }));
        setAdditionalOptions(loaded);
      }

      initializedRef.current = true;
    }
  }, [isEdit, giftItemInfoStatus, giftItemInfoData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (
      [
        'priceRegular',
        'priceRetail',
        'priceWhole',
        'minOrderQuantity',
        'orderUnit',
      ].includes(name)
    ) {
      setFormData((prev) => ({ ...prev, [name]: Number(value) }));
    } else if (
      ['order', 'deliveryShippingFee', 'deliveryFreeShippingMinQuantity'].includes(name)
    ) {
      setFormData((prev) => ({ ...prev, [name]: value === '' ? '' : Number(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const onHeaderImageUploaderFunc = async (_data: unknown, files: File[]) => {
    setHeaderImageFiles(files);
    const previews = files.map((file) => URL.createObjectURL(file));
    setHeaderImagePreviews(previews);
    setHeaderImageUploaderOpen(false);
  };

  const onDetailImageUploaderFunc = async (_data: unknown, files: File[]) => {
    setDetailImageFiles(files);
    // 미리보기 생성
    const previews = files.map((file) => URL.createObjectURL(file));
    setDetailImagePreviews(previews);
    setDetailImageUploaderOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      alert('상품명을 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      let imageId: string | undefined;
      let headerImageIds: string[] | undefined;
      let detailImageIds: string[] | undefined;

      // 대표 이미지 업로드
      if (imageFile) {
        const uploadedImage = await postImage(imageFile, sellerRole());
        imageId = uploadedImage._id;
      }

      // 헤더 이미지 업로드 (순서 유지)
      if (headerImageFiles && headerImageFiles.length > 0) {
        headerImageIds = [];
        for (const file of headerImageFiles) {
          const uploaded = await postImage(file, sellerRole());
          headerImageIds.push(uploaded._id);
        }
      }

      // 상세 이미지 업로드 (순서 유지)
      if (detailImageFiles && detailImageFiles.length > 0) {
        detailImageIds = [];
        for (const file of detailImageFiles) {
          const uploaded = await postImage(file, sellerRole());
          detailImageIds.push(uploaded._id);
        }
      }

      const tagsArray = formData.tags
        ? formData.tags
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean)
        : [];

      // 쿠폰 상세 정보
      const couponDetail =
        formData.type === GiftItemInfoType.Coupon &&
        (formData.couponValidPeriod || formData.couponPlaceOfExchange)
          ? {
              validPeriod: formData.couponValidPeriod,
              placeOfExchange: formData.couponPlaceOfExchange,
            }
          : undefined;

      // 배송 상세 정보
      const hasDeliveryDetail =
        formData.deliveryShippingInfo ||
        formData.deliveryShippingFeeInfo ||
        formData.deliveryShippingFee !== '' ||
        formData.deliveryFreeShippingMinQuantity !== '';
      const deliveryDetail = hasDeliveryDetail
        ? {
            ...(formData.deliveryShippingInfo && {
              shippingInfo: formData.deliveryShippingInfo,
            }),
            ...(formData.deliveryShippingFeeInfo && {
              shippingFeeInfo: formData.deliveryShippingFeeInfo,
            }),
            ...(formData.deliveryShippingFee !== '' && {
              shippingFee: Number(formData.deliveryShippingFee),
            }),
            ...(formData.deliveryFreeShippingMinQuantity !== '' && {
              freeShippingMinQuantity: Number(formData.deliveryFreeShippingMinQuantity),
            }),
          }
        : undefined;

      // 추가옵션 구성 — 신규 이미지 파일은 업로드 후 id로 변환, order는 화면 순서대로.
      // 이름 없는 옵션/선택종류는 제외한다. (배송형이 아니면 비움)
      const builtAdditionalOptions: AdditionalOption<string>[] = [];
      if (formData.type === GiftItemInfoType.Delivery) {
        for (let oi = 0; oi < additionalOptions.length; oi++) {
          const opt = additionalOptions[oi];
          if (!opt) continue;
          const optName = opt.name.trim();
          if (!optName) continue;

          const builtChoices = [];
          for (let ci = 0; ci < opt.choices.length; ci++) {
            const c = opt.choices[ci];
            if (!c) continue;
            const choiceName = c.name.trim();
            if (!choiceName) continue;

            let descriptionImageId = c.descriptionImage?._id;
            if (c.file) {
              const uploaded = await postImage(c.file, sellerRole());
              descriptionImageId = uploaded._id;
            }

            builtChoices.push({
              name: choiceName,
              priceDelta: Number(c.priceDelta) || 0,
              ...(c.description.trim() && { description: c.description.trim() }),
              ...(c.colorHex.trim() && { colorHex: c.colorHex.trim() }),
              ...(descriptionImageId && { descriptionImage: descriptionImageId }),
              order: ci,
              enabled: c.enabled,
            });
          }

          if (builtChoices.length === 0) continue;

          // 슬롯 수(기본 1). 2 이상이면 비운 슬롯 라벨은 제외하고 trim
          const selectionCount = Math.max(1, Math.floor(opt.selectionCount) || 1);
          const slotLabels = Array.from({ length: selectionCount }, (_, i) =>
            (opt.slotLabels[i] ?? '').trim(),
          );
          const hasSlotLabels = selectionCount > 1 && slotLabels.some((l) => l);

          builtAdditionalOptions.push({
            name: optName,
            required: opt.required,
            ...(selectionCount > 1 && { selectionCount }),
            ...(hasSlotLabels && { slotLabels }),
            order: oi,
            choices: builtChoices,
          });
        }
      }

      if (isEdit) {
        const updateData: GiftItemInfoUpdate = {
          _id: itemInfoId,
          type: formData.type,
          giftItemBrand: formData.giftItemBrand || null,
          giftDeliveryCategory: formData.giftDeliveryCategory || null,
          additionalOptions: builtAdditionalOptions,
          name: formData.name,
          priceRegular: formData.priceRegular,
          priceRetail: formData.priceRetail,
          priceWhole: formData.priceWhole,
          isEnabled: formData.isEnabled,
          isSelectable: formData.isSelectable,
          isTaxable: formData.isTaxable,
          choiceUnits: formData.isSelectable ? selectedChoiceUnits : [],
          detailInfo: formData.detailInfo || undefined,
          tags: tagsArray,
          image: imageId,
          headerImages: headerImageIds,
          detailImages: detailImageIds,
          couponDetail,
          deliveryDetail,
          allowIndividualDelivery: formData.allowIndividualDelivery,
          minOrderQuantity: formData.minOrderQuantity,
          orderUnit: formData.orderUnit,
          order: formData.order !== '' ? formData.order : undefined,
          productCode: formData.productCode !== '' ? formData.productCode : undefined,
        };
        await updateGiftItemInfo(updateData, sellerRole());
        alert('원상품이 수정되었습니다.');
      } else {
        const createData: GiftItemInfoCreate = {
          type: formData.type,
          giftItemBrand: formData.giftItemBrand || undefined,
          giftDeliveryCategory: formData.giftDeliveryCategory || undefined,
          additionalOptions:
            builtAdditionalOptions.length > 0 ? builtAdditionalOptions : undefined,
          name: formData.name,
          priceRegular: formData.priceRegular,
          priceRetail: formData.priceRetail,
          priceWhole: formData.priceWhole,
          isEnabled: formData.isEnabled,
          isSelectable: formData.isSelectable,
          isTaxable: formData.isTaxable,
          choiceUnits: formData.isSelectable ? selectedChoiceUnits : [],
          detailInfo: formData.detailInfo || undefined,
          tags: tagsArray.length > 0 ? tagsArray : undefined,
          image: imageId,
          headerImages: headerImageIds,
          detailImages: detailImageIds,
          couponDetail,
          deliveryDetail,
          allowIndividualDelivery: formData.allowIndividualDelivery,
          minOrderQuantity: formData.minOrderQuantity,
          orderUnit: formData.orderUnit,
          order: formData.order !== '' ? formData.order : undefined,
          productCode: formData.productCode !== '' ? formData.productCode : undefined,
        };
        await createGiftItemInfo(createData, sellerRole());
        alert('원상품이 등록되었습니다.');
      }
      navigate('/gift-seller/item-info');
    } catch (error) {
      console.error(error);
      alert('저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;

    if (!confirm('정말 삭제하시겠습니까?')) return;

    setLoading(true);
    try {
      await deleteGiftItemInfo(itemInfoId, sellerRole());
      alert('원상품이 삭제되었습니다.');
      navigate('/gift-seller/item-info');
    } catch (error) {
      console.error(error);
      alert('삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 기존 대표 이미지 URL
  const defaultImageUrl = useMemo(() => {
    if (giftItemInfoData?.image && typeof giftItemInfoData.image === 'object') {
      return (giftItemInfoData.image as Image).url;
    }
    return undefined;
  }, [giftItemInfoData]);

  // 기존 헤더 이미지 목록
  const existingHeaderImages = useMemo(() => {
    if (giftItemInfoData?.headerImages && Array.isArray(giftItemInfoData.headerImages)) {
      return giftItemInfoData.headerImages as Image[];
    }
    return [];
  }, [giftItemInfoData]);

  // 기존 상세 이미지 목록
  const existingDetailImages = useMemo(() => {
    if (giftItemInfoData?.detailImages && Array.isArray(giftItemInfoData.detailImages)) {
      return giftItemInfoData.detailImages as Image[];
    }
    return [];
  }, [giftItemInfoData]);

  if (isEdit && giftItemInfoStatus !== 'success') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-800"
          >
            ←
          </button>
          <h1 className="text-xl font-bold">{isEdit ? '원상품 수정' : '원상품 등록'}</h1>
        </div>
        {isEdit && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? '저장 중...' : '수정하기'}
            </button>
            <button
              onClick={handleDelete}
              className="rounded border border-red-300 px-4 py-1.5 text-sm font-medium text-red-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
              disabled={loading}
            >
              삭제
            </button>
          </div>
        )}
      </div>

      {/* Form */}
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg bg-white p-6 shadow-sm">
          {/* 기본 정보 섹션 */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* 왼쪽: 기본 정보 */}
            <div className="space-y-4">
              {/* 타입 + 활성화 */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    상품 타입
                  </label>
                  <div className="flex gap-4">
                    <label className="flex cursor-pointer items-center gap-1.5">
                      <input
                        type="radio"
                        name="type"
                        value={GiftItemInfoType.Delivery}
                        checked={formData.type === GiftItemInfoType.Delivery}
                        onChange={handleChange}
                        className="h-4 w-4"
                      />
                      <span className="text-sm">배송형</span>
                    </label>
                    <label className="flex cursor-pointer items-center gap-1.5">
                      <input
                        type="radio"
                        name="type"
                        value={GiftItemInfoType.Coupon}
                        checked={formData.type === GiftItemInfoType.Coupon}
                        onChange={handleChange}
                        className="h-4 w-4"
                      />
                      <span className="text-sm">쿠폰형</span>
                    </label>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex cursor-pointer items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">과세</span>
                    <input
                      type="checkbox"
                      name="isTaxable"
                      checked={formData.isTaxable}
                      onChange={handleChange}
                      className="h-4 w-4"
                    />
                  </label>
                  <label className="flex cursor-pointer items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">활성화</span>
                    <input
                      type="checkbox"
                      name="isEnabled"
                      checked={formData.isEnabled}
                      onChange={handleChange}
                      className="h-4 w-4"
                    />
                  </label>
                </div>
              </div>

              {/* 상품 브랜드 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  상품 브랜드
                </label>
                <select
                  name="giftItemBrand"
                  value={formData.giftItemBrand}
                  onChange={handleChange}
                  className="w-full rounded border px-3 py-2 text-sm"
                  disabled={brandListStatus !== 'success'}
                >
                  <option value="">선택 안함</option>
                  {brandList.map((brand) => (
                    <option key={brand._id} value={brand._id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
                <p className="mt-0.5 text-xs text-gray-400">
                  예: 스타벅스, 투썸플레이스 (선택 안함으로 브랜드 제거 가능)
                </p>
              </div>

              {/* 배송 카테고리 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  배송 카테고리
                </label>
                <select
                  name="giftDeliveryCategory"
                  value={formData.giftDeliveryCategory}
                  onChange={handleChange}
                  className="w-full rounded border px-3 py-2 text-sm"
                  disabled={deliveryCategoryListStatus !== 'success'}
                >
                  <option value="">선택 안함</option>
                  {deliveryCategoryList.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <p className="mt-0.5 text-xs text-gray-400">
                  배송 카테고리별 그룹핑에 사용됩니다.
                </p>
              </div>

              {/* 선택형 상품 */}
              <div className="rounded border bg-indigo-50 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-indigo-700">
                      선택형 상품
                    </label>
                    <p className="text-xs text-indigo-500">
                      고객이 교환 시 원하는 옵션을 선택할 수 있습니다
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    name="isSelectable"
                    checked={formData.isSelectable}
                    onChange={handleChange}
                    className="h-5 w-5"
                  />
                </div>

                {/* 선택유닛 선택 (isSelectable일 때만) */}
                {formData.isSelectable && (
                  <div className="mt-3 border-t border-indigo-200 pt-3">
                    <label className="mb-2 block text-sm font-medium text-indigo-700">
                      선택 가능한 유닛
                    </label>

                    {/* 가격대 필터 */}
                    {choiceUnitListStatus === 'success' && choiceUnitList.length > 0 && (
                      <div className="mb-2 flex items-center gap-2">
                        <span className="text-xs font-medium text-indigo-600">
                          가격대
                        </span>
                        <input
                          type="number"
                          value={priceFilterMin}
                          onChange={(e) => setPriceFilterMin(e.target.value)}
                          placeholder="최소"
                          min={0}
                          className="w-24 rounded border border-indigo-200 px-2 py-1 text-xs"
                        />
                        <span className="text-xs text-gray-400">~</span>
                        <input
                          type="number"
                          value={priceFilterMax}
                          onChange={(e) => setPriceFilterMax(e.target.value)}
                          placeholder="최대"
                          min={0}
                          className="w-24 rounded border border-indigo-200 px-2 py-1 text-xs"
                        />
                        <span className="text-xs text-gray-400">원</span>
                        {(priceFilterMin !== '' || priceFilterMax !== '') && (
                          <button
                            type="button"
                            onClick={() => {
                              setPriceFilterMin('');
                              setPriceFilterMax('');
                            }}
                            className="text-xs text-indigo-500 hover:text-indigo-700"
                          >
                            초기화
                          </button>
                        )}
                      </div>
                    )}

                    {choiceUnitListStatus === 'success' && choiceUnitList.length > 0 ? (
                      <div className="max-h-48 space-y-2 overflow-y-auto">
                        {filteredChoiceUnitList.length === 0 ? (
                          <p className="py-2 text-center text-xs text-gray-400">
                            해당 가격대의 유닛이 없습니다.
                          </p>
                        ) : null}
                        {filteredChoiceUnitList.map((unit) => {
                          const isSelected = selectedChoiceUnits.includes(unit._id);
                          return (
                            <label
                              key={unit._id}
                              className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2 ${
                                isSelected
                                  ? 'border-indigo-400 bg-indigo-100'
                                  : 'border-gray-200 bg-white hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedChoiceUnits((prev) => [...prev, unit._id]);
                                  } else {
                                    setSelectedChoiceUnits((prev) =>
                                      prev.filter((id) => id !== unit._id),
                                    );
                                  }
                                }}
                                className="h-4 w-4"
                              />
                              {unit.image && typeof unit.image === 'object' && (
                                <img
                                  src={fileBucketUrl(unit.image.url, 'raw')}
                                  alt={unit.name}
                                  className="h-8 w-8 rounded object-cover"
                                />
                              )}
                              <div className="flex-1">
                                <p className="text-sm font-medium">{unit.name}</p>
                                <div className="flex items-center gap-2">
                                  {unit.giftItemBrand &&
                                    typeof unit.giftItemBrand === 'object' && (
                                      <span className="text-xs text-gray-500">
                                        {unit.giftItemBrand.name}
                                      </span>
                                    )}
                                  {unit.priceRegular != null && (
                                    <span className="text-xs text-indigo-500">
                                      {unit.priceRegular.toLocaleString()}원
                                    </span>
                                  )}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ) : choiceUnitListStatus === 'loading' ? (
                      <p className="text-xs text-gray-500">로딩 중...</p>
                    ) : (
                      <p className="text-xs text-gray-500">
                        등록된 선택유닛이 없습니다.{' '}
                        <a
                          href="/gift-seller/choice-unit/new"
                          className="text-indigo-600 underline"
                        >
                          선택유닛 등록
                        </a>
                      </p>
                    )}

                    {/* 선택된 유닛 순서 관리 */}
                    {selectedChoiceUnits.length > 0 && (
                      <div className="mt-3 border-t border-indigo-200 pt-3">
                        <label className="mb-1 block text-xs font-medium text-indigo-700">
                          표시 순서 (위에서부터 순서대로 표시)
                        </label>
                        <div className="space-y-1">
                          {selectedChoiceUnits.map((unitId, idx) => {
                            const unit = choiceUnitList.find((u) => u._id === unitId);
                            if (!unit) return null;
                            return (
                              <div
                                key={unitId}
                                className="flex items-center gap-2 rounded border border-indigo-200 bg-white px-2 py-1.5"
                              >
                                <span className="w-5 text-center text-xs font-medium text-indigo-500">
                                  {idx + 1}
                                </span>
                                {unit.image && typeof unit.image === 'object' && (
                                  <img
                                    src={fileBucketUrl(unit.image.url, 'raw')}
                                    alt={unit.name}
                                    className="h-6 w-6 rounded object-cover"
                                  />
                                )}
                                <span className="flex-1 truncate text-xs">
                                  {unit.name}
                                </span>
                                <div className="flex gap-0.5">
                                  <button
                                    type="button"
                                    disabled={idx === 0}
                                    onClick={() => {
                                      setSelectedChoiceUnits((prev) => {
                                        const next = [...prev];
                                        const tmp = next[idx - 1] as string;
                                        next[idx - 1] = next[idx] as string;
                                        next[idx] = tmp;
                                        return next;
                                      });
                                    }}
                                    className="rounded px-1.5 py-0.5 text-xs text-indigo-600 hover:bg-indigo-100 disabled:text-gray-300 disabled:hover:bg-transparent"
                                  >
                                    ▲
                                  </button>
                                  <button
                                    type="button"
                                    disabled={idx === selectedChoiceUnits.length - 1}
                                    onClick={() => {
                                      setSelectedChoiceUnits((prev) => {
                                        const next = [...prev];
                                        const tmp = next[idx] as string;
                                        next[idx] = next[idx + 1] as string;
                                        next[idx + 1] = tmp;
                                        return next;
                                      });
                                    }}
                                    className="rounded px-1.5 py-0.5 text-xs text-indigo-600 hover:bg-indigo-100 disabled:text-gray-300 disabled:hover:bg-transparent"
                                  >
                                    ▼
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedChoiceUnits((prev) =>
                                        prev.filter((id) => id !== unitId),
                                      );
                                    }}
                                    className="rounded px-1.5 py-0.5 text-xs text-red-500 hover:bg-red-50"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <p className="mt-1 text-[10px] text-gray-400">
                          ▲▼ 버튼으로 순서를 변경하세요. 이 순서대로 고객에게 표시됩니다.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 상품코드 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  상품코드
                </label>
                <input
                  name="productCode"
                  value={formData.productCode}
                  onChange={handleChange}
                  placeholder="상품코드를 입력하세요"
                  className="w-full rounded border px-3 py-2 text-sm"
                />
              </div>

              {/* 상품명 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  상품명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="상품명을 입력하세요"
                  className="w-full rounded border px-3 py-2 text-sm"
                  required
                />
              </div>

              {/* 가격 설정 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  가격 설정 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">정가</label>
                    <input
                      type="number"
                      name="priceRegular"
                      value={formData.priceRegular}
                      onChange={handleChange}
                      placeholder="0"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">판매가</label>
                    <input
                      type="number"
                      name="priceRetail"
                      value={formData.priceRetail}
                      onChange={handleChange}
                      placeholder="0"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">도매가</label>
                    <input
                      type="number"
                      name="priceWhole"
                      value={formData.priceWhole}
                      onChange={handleChange}
                      placeholder="0"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* 주문 설정 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  주문 설정
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">
                      최소주문수량
                    </label>
                    <input
                      type="number"
                      name="minOrderQuantity"
                      value={formData.minOrderQuantity}
                      onChange={handleChange}
                      min={1}
                      placeholder="1"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">주문단위</label>
                    <input
                      type="number"
                      name="orderUnit"
                      value={formData.orderUnit}
                      onChange={handleChange}
                      min={1}
                      placeholder="1"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                    />
                    <p className="mt-0.5 text-[10px] text-gray-400">(수량 증감 단위)</p>
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">표시순서</label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleChange}
                      min={0}
                      placeholder="미설정"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                    />
                    <p className="mt-0.5 text-[10px] text-gray-400">
                      (낮을수록 앞에 표시)
                    </p>
                  </div>
                </div>
              </div>

              {/* 태그 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  태그
                </label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  placeholder="쉼표로 구분 (예: 답례품, 인기상품)"
                  className="w-full rounded border px-3 py-2 text-sm"
                />
              </div>

              {/* 쿠폰 상세 정보 (쿠폰형일 때만) */}
              {formData.type === GiftItemInfoType.Coupon && (
                <div className="rounded border bg-purple-50 p-3">
                  <label className="mb-2 block text-sm font-medium text-purple-700">
                    쿠폰 상세 정보
                  </label>
                  <div className="space-y-2">
                    <div>
                      <label className="mb-0.5 block text-xs text-gray-600">
                        유효기간
                      </label>
                      <input
                        type="text"
                        name="couponValidPeriod"
                        value={formData.couponValidPeriod}
                        onChange={handleChange}
                        placeholder="예: 발행일로부터 3개월"
                        className="w-full rounded border px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-0.5 block text-xs text-gray-600">교환처</label>
                      <input
                        type="text"
                        name="couponPlaceOfExchange"
                        value={formData.couponPlaceOfExchange}
                        onChange={handleChange}
                        placeholder="예: 전국 CU 편의점"
                        className="w-full rounded border px-2 py-1.5 text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* 상세 설명 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  상세 설명
                </label>
                <textarea
                  name="detailInfo"
                  value={formData.detailInfo}
                  onChange={handleChange}
                  placeholder="상세 설명을 입력하세요"
                  className="h-24 w-full rounded border px-3 py-2 text-sm"
                />
              </div>

              {/* 배송 상세 정보 (배송형일 때만) */}
              {formData.type === GiftItemInfoType.Delivery && (
                <div className="rounded border bg-blue-50 p-3">
                  <label className="mb-2 block text-sm font-medium text-blue-700">
                    배송 상세 정보
                  </label>
                  {selectedBrand?.deliveryDetail && (
                    <p className="mb-2 text-xs text-blue-500">
                      브랜드 기본값이 설정되어 있습니다. 비워두면 브랜드 기본값이
                      적용됩니다.
                    </p>
                  )}

                  {/* 개별배송 허용 여부 */}
                  <div className="mb-3 flex items-start justify-between gap-2 rounded border border-blue-200 bg-white p-2">
                    <div>
                      <label className="text-sm font-medium text-blue-700">
                        개별배송 허용
                      </label>
                      <p className="text-xs text-blue-500">
                        끄면 이 상품은 일괄배송(Bulk) 전용이 되어 개별배송 선택지가
                        숨겨집니다.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      name="allowIndividualDelivery"
                      checked={formData.allowIndividualDelivery}
                      onChange={handleChange}
                      className="mt-0.5 h-5 w-5"
                    />
                  </div>

                  <div className="space-y-2">
                    <div>
                      <label className="mb-0.5 block text-xs text-gray-600">
                        배송정보/기간 안내
                      </label>
                      <textarea
                        name="deliveryShippingInfo"
                        value={formData.deliveryShippingInfo}
                        onChange={handleChange}
                        placeholder={
                          selectedBrand?.deliveryDetail?.shippingInfo ||
                          '예: 주문 후 2~3일 이내 출고'
                        }
                        className="h-20 w-full rounded border px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-0.5 block text-xs text-gray-600">
                        배송비 안내
                      </label>
                      <textarea
                        name="deliveryShippingFeeInfo"
                        value={formData.deliveryShippingFeeInfo}
                        onChange={handleChange}
                        placeholder={
                          selectedBrand?.deliveryDetail?.shippingFeeInfo ||
                          '예: 무료배송 / 3,000원'
                        }
                        className="h-20 w-full rounded border px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="mb-0.5 block text-xs text-gray-600">
                          소량주문 추가금 (개당)
                        </label>
                        <input
                          type="number"
                          name="deliveryShippingFee"
                          value={formData.deliveryShippingFee}
                          onChange={handleChange}
                          min={0}
                          placeholder={
                            selectedBrand?.deliveryDetail?.shippingFee != null
                              ? `브랜드 기본 ${selectedBrand.deliveryDetail.shippingFee}원`
                              : '예: 500'
                          }
                          className="w-full rounded border px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-0.5 block text-xs text-gray-600">
                          추가금 면제 최소수량
                        </label>
                        <input
                          type="number"
                          name="deliveryFreeShippingMinQuantity"
                          value={formData.deliveryFreeShippingMinQuantity}
                          onChange={handleChange}
                          min={0}
                          placeholder={
                            selectedBrand?.deliveryDetail?.freeShippingMinQuantity != null
                              ? `브랜드 기본 ${selectedBrand.deliveryDetail.freeShippingMinQuantity}개`
                              : '예: 20'
                          }
                          className="w-full rounded border px-2 py-1.5 text-sm"
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-400">
                      이 수량 미만 주문 시 <b>개당</b> 추가금이 단가에 붙습니다(이상이면 면제).
                      비워두면 브랜드 기본값이 적용됩니다.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* 오른쪽: 이미지 */}
            <div className="space-y-4">
              {/* 대표 이미지 */}
              <div className="max-w-[400px]">
                <ImageUploadBox
                  title="대표 이미지"
                  setFile={setImageFile}
                  defaultImageUrl={defaultImageUrl}
                />
              </div>

              {/* 헤더 이미지 (정사각형, carousel용) */}
              <div className="max-w-[400px]">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  헤더 이미지 (정사각형, carousel용)
                </label>
                <p className="mb-1 text-xs text-gray-500">
                  상세페이지 상단에 스크롤 형태로 표시됩니다. 순서대로 정렬됩니다.
                </p>
                <Button
                  type="button"
                  className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white"
                  onClick={() => {
                    setHeaderImageUploaderOpen(true);
                  }}
                >
                  이미지 업로드
                </Button>

                {/* 미리보기 또는 기존 이미지 */}
                {headerImagePreviews.length > 0 ? (
                  <div className="mt-2 flex gap-2 overflow-x-auto">
                    {headerImagePreviews.map((preview, idx) => (
                      <div key={idx} className="relative flex-shrink-0">
                        <img
                          src={preview}
                          alt={`헤더이미지 ${idx + 1}`}
                          className="h-20 w-20 rounded object-cover"
                        />
                        <span className="absolute left-1 top-1 rounded bg-black/50 px-1 text-xs text-white">
                          {idx + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : existingHeaderImages.length > 0 ? (
                  <div className="mt-2 flex gap-2 overflow-x-auto">
                    {existingHeaderImages.map((img, idx) => (
                      <div key={idx} className="relative flex-shrink-0">
                        <img
                          src={fileBucketUrl(img.url, 'raw')}
                          alt={`헤더이미지 ${idx + 1}`}
                          className="h-20 w-20 rounded object-cover"
                        />
                        <span className="absolute left-1 top-1 rounded bg-black/50 px-1 text-xs text-white">
                          {idx + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-gray-400">
                    등록된 헤더 이미지가 없습니다.
                  </p>
                )}
              </div>

              {/* 상세 이미지 */}
              <div className="max-w-[400px]">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  상세 이미지 (세로로 이어붙임)
                </label>
                <p className="mb-1 text-xs text-gray-500">
                  상세페이지 하단에 순서대로 세로로 표시됩니다.
                </p>
                <Button
                  type="button"
                  className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white"
                  onClick={() => setDetailImageUploaderOpen(true)}
                >
                  이미지 업로드
                </Button>

                {/* 미리보기 또는 기존 이미지 */}
                {detailImagePreviews.length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {detailImagePreviews.map((preview, idx) => (
                      <div key={idx} className="relative">
                        <img
                          src={preview}
                          alt={`상세이미지 ${idx + 1}`}
                          className="w-full rounded object-cover"
                        />
                        <span className="absolute left-1 top-1 rounded bg-black/50 px-1 text-xs text-white">
                          {idx + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : existingDetailImages.length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {existingDetailImages.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img
                          src={fileBucketUrl(img.url, 'w400')}
                          alt={`상세이미지 ${idx + 1}`}
                          className="w-full rounded object-cover"
                        />
                        <span className="absolute left-1 top-1 rounded bg-black/50 px-1 text-xs text-white">
                          {idx + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-gray-400">
                    등록된 상세 이미지가 없습니다.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* 추가옵션 (배송형 전용) */}
          {formData.type === GiftItemInfoType.Delivery && (
            <div className="mt-6 border-t pt-6">
              <div className="mb-3 flex items-start justify-between gap-2">
                <div>
                  <h2 className="text-base font-semibold">추가옵션</h2>
                  <p className="mt-0.5 text-xs text-gray-500">
                    비누종류·향초종류처럼 선택종류별 추가금이 붙는 옵션입니다. 추가금이
                    0이면 무료로 표시됩니다. 없으면 비워두세요.
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    type="button"
                    onClick={handleCopyOptions}
                    disabled={additionalOptions.length === 0}
                    className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                  >
                    복사
                  </Button>
                  <Button
                    type="button"
                    onClick={handlePasteOptions}
                    disabled={clipboardCount === 0}
                    className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                  >
                    붙여넣기{clipboardCount > 0 ? ` (${clipboardCount})` : ''}
                  </Button>
                  <Button
                    type="button"
                    onClick={addOption}
                    className="rounded border border-blue-300 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50"
                  >
                    + 옵션 그룹 추가
                  </Button>
                </div>
              </div>

              {additionalOptions.length === 0 ? (
                <p className="rounded-lg border border-dashed border-gray-300 py-6 text-center text-sm text-gray-400">
                  등록된 추가옵션이 없습니다.
                </p>
              ) : (
                <div className="space-y-4">
                  {additionalOptions.map((opt, optIdx) => (
                    <div
                      key={opt.key}
                      className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                    >
                      {/* 옵션 그룹 헤더 */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-700">
                          옵션 {optIdx + 1}
                        </span>
                        <input
                          type="text"
                          value={opt.name}
                          onChange={(e) =>
                            updateOption(opt.key, { name: e.target.value })
                          }
                          placeholder="옵션명 (예: 비누종류)"
                          className="min-w-[140px] flex-1 rounded border px-2 py-1.5 text-sm"
                        />
                        <label className="flex shrink-0 cursor-pointer items-center gap-1 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={opt.required}
                            onChange={(e) =>
                              updateOption(opt.key, { required: e.target.checked })
                            }
                            className="h-4 w-4"
                          />
                          필수 선택
                        </label>
                        <div className="flex shrink-0 gap-0.5">
                          <button
                            type="button"
                            disabled={optIdx === 0}
                            onClick={() => moveOption(opt.key, -1)}
                            className="rounded px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent"
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            disabled={optIdx === additionalOptions.length - 1}
                            onClick={() => moveOption(opt.key, 1)}
                            className="rounded px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent"
                          >
                            ▼
                          </button>
                          <button
                            type="button"
                            onClick={() => removeOption(opt.key)}
                            className="rounded px-1.5 py-0.5 text-xs text-red-500 hover:bg-red-50"
                          >
                            ✕
                          </button>
                        </div>
                      </div>

                      {/* 선택 횟수(슬롯) 설정 */}
                      <div className="mt-2 flex flex-wrap items-center gap-2 rounded border border-dashed border-blue-200 bg-blue-50/40 px-2 py-1.5">
                        <span className="text-xs font-medium text-gray-600">선택 횟수</span>
                        <input
                          type="number"
                          min={1}
                          max={10}
                          value={opt.selectionCount}
                          onChange={(e) => {
                            const n = Math.max(
                              1,
                              Math.min(10, Math.floor(Number(e.target.value)) || 1),
                            );
                            updateOption(opt.key, { selectionCount: n });
                          }}
                          className="w-16 rounded border px-2 py-1 text-sm"
                        />
                        <span className="text-[11px] text-gray-400">
                          2 이상이면 같은 선택종류를 횟수만큼 각각 고릅니다 (예:
                          비누종류1·비누종류2)
                        </span>
                      </div>

                      {/* 슬롯 라벨 (선택 횟수 2 이상일 때) */}
                      {opt.selectionCount > 1 && (
                        <div className="mt-2">
                          <p className="mb-1 text-[11px] text-gray-500">
                            슬롯 라벨 (비우면 「{opt.name || '옵션'}1·2…」 자동 생성)
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {Array.from({ length: opt.selectionCount }).map((_, si) => (
                              <input
                                key={si}
                                type="text"
                                value={opt.slotLabels[si] ?? ''}
                                onChange={(e) => {
                                  const labels = Array.from(
                                    { length: opt.selectionCount },
                                    (__, i) => opt.slotLabels[i] ?? '',
                                  );
                                  labels[si] = e.target.value;
                                  updateOption(opt.key, { slotLabels: labels });
                                }}
                                placeholder={`${opt.name || '옵션'}${si + 1}`}
                                className="w-32 rounded border px-2 py-1 text-sm"
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 선택종류 목록 */}
                      <div className="mt-3 space-y-2">
                        {opt.choices.map((c, cIdx) => {
                          const imgUrl = choiceImageUrl(c);
                          return (
                            <div
                              key={c.key}
                              className={`rounded-lg border border-gray-200 bg-white p-2 ${
                                c.enabled ? '' : 'opacity-60'
                              }`}
                            >
                              <div className="flex flex-wrap items-center gap-2">
                              {/* 설명 이미지 업로드 */}
                              <label className="relative flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded border border-dashed border-gray-300 bg-gray-50 text-[10px] text-gray-400 hover:border-blue-400">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) handleChoiceFile(opt.key, c.key, f);
                                  }}
                                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                />
                                {imgUrl ? (
                                  <img
                                    src={imgUrl}
                                    alt={c.name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <span>이미지</span>
                                )}
                              </label>

                              <input
                                type="text"
                                value={c.name}
                                onChange={(e) =>
                                  updateChoice(opt.key, c.key, { name: e.target.value })
                                }
                                placeholder="선택종류명 (예: 골든)"
                                className="min-w-[120px] flex-1 rounded border px-2 py-1.5 text-sm"
                              />
                              <div className="flex shrink-0 items-center gap-1">
                                <span className="text-xs text-gray-400">+</span>
                                <input
                                  type="number"
                                  value={c.priceDelta}
                                  onChange={(e) =>
                                    updateChoice(opt.key, c.key, {
                                      priceDelta: Number(e.target.value),
                                    })
                                  }
                                  min={0}
                                  placeholder="추가금"
                                  className="w-24 rounded border px-2 py-1.5 text-sm"
                                />
                                <span className="text-xs text-gray-400">원</span>
                              </div>
                              <label className="flex shrink-0 items-center gap-1 text-xs text-gray-500">
                                <input
                                  type="color"
                                  value={c.colorHex || '#000000'}
                                  onChange={(e) =>
                                    updateChoice(opt.key, c.key, {
                                      colorHex: e.target.value,
                                    })
                                  }
                                  className="h-7 w-7 cursor-pointer rounded border border-gray-300 bg-white p-0.5"
                                  title="색상칩"
                                />
                                {c.colorHex ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateChoice(opt.key, c.key, { colorHex: '' })
                                    }
                                    className="underline"
                                  >
                                    제거
                                  </button>
                                ) : (
                                  <span>색상</span>
                                )}
                              </label>
                              <label className="flex shrink-0 cursor-pointer items-center gap-1 text-xs text-gray-600">
                                <input
                                  type="checkbox"
                                  checked={c.enabled}
                                  onChange={(e) =>
                                    updateChoice(opt.key, c.key, {
                                      enabled: e.target.checked,
                                    })
                                  }
                                  className="h-4 w-4"
                                />
                                노출
                              </label>
                              <div className="flex shrink-0 gap-0.5">
                                <button
                                  type="button"
                                  disabled={cIdx === 0}
                                  onClick={() => moveChoice(opt.key, c.key, -1)}
                                  className="rounded px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent"
                                >
                                  ▲
                                </button>
                                <button
                                  type="button"
                                  disabled={cIdx === opt.choices.length - 1}
                                  onClick={() => moveChoice(opt.key, c.key, 1)}
                                  className="rounded px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent"
                                >
                                  ▼
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeChoice(opt.key, c.key)}
                                  className="rounded px-1.5 py-0.5 text-xs text-red-500 hover:bg-red-50"
                                >
                                  ✕
                                </button>
                              </div>
                              </div>

                              {/* 한 줄 설명 (전체 폭) */}
                              <input
                                type="text"
                                value={c.description}
                                onChange={(e) =>
                                  updateChoice(opt.key, c.key, {
                                    description: e.target.value,
                                  })
                                }
                                placeholder="설명 (선택) — 예: 은은한 라벤더 향, 천연 유래 성분"
                                className="mt-2 w-full rounded border px-2 py-1.5 text-sm"
                              />
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() => addChoice(opt.key)}
                          className="w-full rounded-lg border border-dashed border-gray-300 py-2 text-xs font-medium text-gray-500 hover:border-blue-400 hover:text-blue-600"
                        >
                          + 선택종류 추가
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 제출 버튼 */}
          <div className="mt-6 border-t pt-4">
            <Button
              type="button"
              disabled={loading}
              onClick={handleSubmit}
              className="rounded bg-blue-600 px-6 py-2 font-medium text-white disabled:bg-gray-400"
            >
              {loading ? '저장 중...' : isEdit ? '수정하기' : '등록하기'}
            </Button>
          </div>
        </div>
      </div>

      {/* 헤더 이미지 업로더 모달 */}
      <ImageUploader
        openId={headerImageOpenId}
        title="헤더 이미지 업로드 (정사각형)"
        onSubmit={onHeaderImageUploaderFunc}
        useName={false}
      />

      {/* 상세 이미지 업로더 모달 */}
      <ImageUploader
        openId={openId}
        title="상세이미지 업로드"
        onSubmit={onDetailImageUploaderFunc}
        useName={false}
      />
    </div>
  );
};
